import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
    CheckSquare, Plus, AlertTriangle, Calendar, Clock, Trash2,
    ChevronDown, ChevronRight, User, Building2, Flag,
    Loader2, Search, Filter, X, Check, Sparkles, Lightbulb,
    Workflow, Mail, Linkedin, Phone,
} from 'lucide-react';
import CampaignTaskExecutionModal from '../components/CampaignTaskExecutionModal';
import { Checkbox } from '../components/ui/checkbox';
import {
    useTasks, useTasksSummary, useCreateTask, useCompleteTask,
    useReopenTask, useDeleteTask, useBulkCompleteTasks, useBulkDeleteTasks,
    useUpdateTask, useSuggestTasks,
} from '../hooks/useTasks';

const PRIORITY_COLORS = {
    high: 'bg-red-50 text-red-700 border-red-200',
    medium: 'bg-amber-50 text-amber-700 border-amber-200',
    low: 'bg-stone-50 text-stone-500 border-stone-200',
};

const DATE_GROUP_CONFIG = {
    overdue: { label: 'Overdue', icon: AlertTriangle, color: 'text-red-600', bg: 'bg-red-50 border-red-200' },
    today: { label: 'Today', icon: Calendar, color: 'text-stone-900', bg: 'bg-blue-50 border-blue-200' },
    tomorrow: { label: 'Tomorrow', icon: Clock, color: 'text-stone-700', bg: 'bg-stone-50 border-stone-200' },
    this_week: { label: 'This Week', icon: Calendar, color: 'text-stone-600', bg: 'bg-stone-50 border-stone-200' },
    later: { label: 'Later', icon: Calendar, color: 'text-stone-500', bg: 'bg-stone-50 border-stone-200' },
};

const GROUP_ORDER = ['overdue', 'today', 'tomorrow', 'this_week', 'later'];

function formatDueDate(dateStr) {
    if (!dateStr) return '';
    const d = new Date(dateStr + 'T00:00:00');
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const tomorrow = new Date(today); tomorrow.setDate(today.getDate() + 1);

    if (d.getTime() === today.getTime()) return 'Today';
    if (d.getTime() === tomorrow.getTime()) return 'Tomorrow';
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function dueDateColor(dateStr) {
    if (!dateStr) return 'text-stone-400';
    const d = new Date(dateStr + 'T00:00:00');
    const today = new Date(); today.setHours(0, 0, 0, 0);
    if (d < today) return 'text-red-600 font-medium';
    if (d.getTime() === today.getTime()) return 'text-amber-600 font-medium';
    return 'text-stone-500';
}

/* ── Quick Add Bar ───────────────────────────────────────────────────────── */
function QuickAddBar() {
    const [title, setTitle] = useState('');
    const [dueDate, setDueDate] = useState('');
    const [priority, setPriority] = useState('medium');
    const [showOptions, setShowOptions] = useState(false);
    const createTask = useCreateTask();

    const addDays = (days) => {
        const d = new Date();
        d.setDate(d.getDate() + days);
        return d.toISOString().split('T')[0];
    };

    const handleSubmit = (e) => {
        if (e) e.preventDefault();
        if (!title.trim()) return;

        createTask.mutate({
            title: title.trim(),
            due_date: dueDate || undefined,
            priority,
            source: 'manual',
        }, {
            onSuccess: () => {
                setTitle('');
                setDueDate('');
                setPriority('medium');
                setShowOptions(false);
                toast.success('Task created');
            },
            onError: () => toast.error('Failed to create task'),
        });
    };

    return (
        <form onSubmit={handleSubmit} className="bg-white rounded-xl border border-stone-200 p-3">
            <div className="flex items-center gap-2">
                <Plus className="w-4 h-4 text-stone-400 shrink-0" />
                <input
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    onFocus={() => setShowOptions(true)}
                    placeholder="Add a task..."
                    className="flex-1 text-sm text-stone-900 placeholder:text-stone-400 focus:outline-none bg-transparent"
                />
                {title.trim() && (
                    <button
                        type="submit"
                        disabled={createTask.isPending}
                        className="px-3 py-1.5 text-xs font-medium text-white bg-stone-900 rounded-lg hover:bg-stone-800 disabled:opacity-50 shrink-0"
                    >
                        {createTask.isPending ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Add'}
                    </button>
                )}
            </div>

            {showOptions && (
                <div className="flex items-center gap-2 mt-2 pt-2 border-t border-stone-100 flex-wrap">
                    {/* Quick date buttons */}
                    <div className="flex items-center gap-1">
                        {[
                            { label: 'Today', date: addDays(0) },
                            { label: 'Tomorrow', date: addDays(1) },
                            { label: 'Next week', date: addDays(7) },
                        ].map(opt => (
                            <button
                                key={opt.label}
                                type="button"
                                onClick={() => setDueDate(opt.date)}
                                className={`px-2 py-1 text-[11px] rounded-md border transition-colors ${
                                    dueDate === opt.date
                                        ? 'bg-stone-900 text-white border-stone-900'
                                        : 'bg-white text-stone-600 border-stone-200 hover:border-stone-300'
                                }`}
                            >
                                {opt.label}
                            </button>
                        ))}
                    </div>

                    {/* Custom date */}
                    <input
                        type="date"
                        value={dueDate}
                        onChange={(e) => setDueDate(e.target.value)}
                        className="px-2 py-1 text-[11px] border border-stone-200 rounded-md focus:outline-none focus:ring-1 focus:ring-stone-400"
                    />

                    {/* Priority */}
                    <select
                        value={priority}
                        onChange={(e) => setPriority(e.target.value)}
                        className="px-2 py-1 text-[11px] border border-stone-200 rounded-md bg-white focus:outline-none"
                    >
                        <option value="low">Low</option>
                        <option value="medium">Medium</option>
                        <option value="high">High</option>
                    </select>

                    <button type="button" onClick={() => setShowOptions(false)} className="ml-auto text-stone-400 hover:text-stone-600">
                        <X className="w-3.5 h-3.5" />
                    </button>
                </div>
            )}
        </form>
    );
}

/* ── Task Row ────────────────────────────────────────────────────────────── */
const SUBTYPE_ICONS = { email: Mail, linkedin: Linkedin, call: Phone };

function TaskRow({ task, isSelected, onSelect, onComplete, onReopen, onDelete, onEdit, onOpenCampaignTask }) {
    const [editing, setEditing] = useState(false);
    const [editTitle, setEditTitle] = useState(task.title);
    const [editDueDate, setEditDueDate] = useState(task.due_date || '');
    const [editPriority, setEditPriority] = useState(task.priority || 'medium');
    const navigate = useNavigate();
    const updateTask = useUpdateTask();
    const isCompleted = task.status === 'completed';
    const isCampaignTask = task.source === 'campaign' && task.task_subtype && task.task_subtype !== 'general';
    const SubtypeIcon = SUBTYPE_ICONS[task.task_subtype] || null;

    const handleToggle = () => {
        if (isCompleted) onReopen(task.activity_id);
        else onComplete(task.activity_id);
    };

    const handleSaveEdit = () => {
        updateTask.mutate({
            activityId: task.activity_id,
            updates: { title: editTitle, due_date: editDueDate || null, priority: editPriority },
        }, {
            onSuccess: () => { setEditing(false); toast.success('Task updated'); },
            onError: () => toast.error('Failed to update'),
        });
    };

    if (editing) {
        return (
            <div className="px-4 py-3 bg-stone-50 border-b border-stone-100 space-y-2">
                <input
                    value={editTitle}
                    onChange={(e) => setEditTitle(e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-stone-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-stone-900/20"
                    autoFocus
                    onKeyDown={(e) => { if (e.key === 'Enter') handleSaveEdit(); if (e.key === 'Escape') setEditing(false); }}
                />
                <div className="flex items-center gap-2">
                    <input type="date" value={editDueDate} onChange={(e) => setEditDueDate(e.target.value)}
                        className="px-2 py-1 text-xs border border-stone-200 rounded-md" />
                    <select value={editPriority} onChange={(e) => setEditPriority(e.target.value)}
                        className="px-2 py-1 text-xs border border-stone-200 rounded-md bg-white">
                        <option value="low">Low</option>
                        <option value="medium">Medium</option>
                        <option value="high">High</option>
                    </select>
                    <button onClick={handleSaveEdit} className="px-2 py-1 text-xs font-medium text-white bg-stone-900 rounded-md hover:bg-stone-800">Save</button>
                    <button onClick={() => setEditing(false)} className="px-2 py-1 text-xs text-stone-500 hover:text-stone-700">Cancel</button>
                </div>
            </div>
        );
    }

    return (
        <div className={`group flex items-center px-4 py-2.5 border-b border-stone-100 hover:bg-stone-50 transition-colors ${
            isSelected ? 'bg-stone-100' : ''
        }`}>
            {/* Select checkbox */}
            <div className="mr-3" onClick={(e) => e.stopPropagation()}>
                <Checkbox checked={isSelected} onCheckedChange={() => onSelect(task.activity_id)} />
            </div>

            {/* Complete checkbox */}
            <button
                onClick={handleToggle}
                className={`w-5 h-5 rounded-full border-2 flex items-center justify-center mr-3 shrink-0 transition-colors ${
                    isCompleted
                        ? 'bg-emerald-500 border-emerald-500 text-white'
                        : 'border-stone-300 hover:border-stone-500'
                }`}
            >
                {isCompleted && <Check className="w-3 h-3" />}
            </button>

            {/* Title + contact + campaign badge */}
            <div
                className={`flex-1 min-w-0 mr-3 ${isCampaignTask ? 'cursor-pointer' : ''}`}
                onClick={() => { if (isCampaignTask && onOpenCampaignTask) onOpenCampaignTask(task); }}
            >
                <div className={`text-sm flex items-center gap-1.5 ${isCompleted ? 'line-through text-stone-400' : 'text-stone-900'}`}>
                    {isCampaignTask && SubtypeIcon && <SubtypeIcon className="w-3.5 h-3.5 text-stone-400 shrink-0" />}
                    {task.title}
                    {isCampaignTask && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-stone-100 text-stone-500 font-medium shrink-0 flex items-center gap-0.5">
                            <Workflow className="w-2.5 h-2.5" /> Campaign
                        </span>
                    )}
                </div>
                {task.contact_name && (
                    <button
                        onClick={(e) => { e.stopPropagation(); navigate(`/contacts/${task.contact_id}`); }}
                        className="flex items-center gap-1 text-[11px] text-stone-400 hover:text-blue-600 mt-0.5"
                    >
                        <User className="w-3 h-3" /> {task.contact_name}
                    </button>
                )}
            </div>

            {/* Due date */}
            {task.due_date && (
                <span className={`text-xs mr-3 shrink-0 ${isCompleted ? 'text-stone-400' : dueDateColor(task.due_date)}`}>
                    {formatDueDate(task.due_date)}
                </span>
            )}

            {/* Priority badge */}
            <span className={`text-[10px] px-1.5 py-0.5 rounded border mr-3 shrink-0 ${
                isCompleted ? 'bg-stone-50 text-stone-400 border-stone-200' : PRIORITY_COLORS[task.priority] || PRIORITY_COLORS.medium
            }`}>
                {task.priority}
            </span>

            {/* Hover actions */}
            <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
                <button onClick={() => setEditing(true)} title="Edit"
                    className="p-1 rounded hover:bg-stone-200 text-stone-400 hover:text-stone-700">
                    <Flag className="w-3 h-3" />
                </button>
                <button onClick={() => onDelete(task.activity_id)} title="Delete"
                    className="p-1 rounded hover:bg-red-100 text-stone-400 hover:text-red-600">
                    <Trash2 className="w-3 h-3" />
                </button>
            </div>
        </div>
    );
}

/* ── Task Group ──────────────────────────────────────────────────────────── */
function TaskGroup({ groupKey, tasks, selectedIds, onSelect, onComplete, onReopen, onDelete, onOpenCampaignTask }) {
    const [collapsed, setCollapsed] = useState(false);
    const config = DATE_GROUP_CONFIG[groupKey] || DATE_GROUP_CONFIG.later;
    const Icon = config.icon;

    if (tasks.length === 0) return null;

    return (
        <div className="mb-4">
            <button
                onClick={() => setCollapsed(!collapsed)}
                className={`flex items-center gap-2 px-4 py-2 w-full text-left rounded-t-lg border ${config.bg}`}
            >
                {collapsed ? <ChevronRight className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                <Icon className={`w-3.5 h-3.5 ${config.color}`} />
                <span className={`text-xs font-semibold ${config.color}`} style={{ fontFamily: 'Manrope, sans-serif' }}>
                    {config.label}
                </span>
                <span className="text-[10px] text-stone-400 ml-1">({tasks.length})</span>
            </button>

            {!collapsed && (
                <div className="bg-white border border-t-0 border-stone-200 rounded-b-lg overflow-hidden">
                    {tasks.map(task => (
                        <TaskRow
                            key={task.activity_id}
                            task={task}
                            isSelected={selectedIds.includes(task.activity_id)}
                            onSelect={onSelect}
                            onComplete={onComplete}
                            onReopen={onReopen}
                            onDelete={onDelete}
                            onOpenCampaignTask={onOpenCampaignTask}
                        />
                    ))}
                </div>
            )}
        </div>
    );
}

/* ── AI Suggestion Panel ────────────────────────────────────────────────── */
function AISuggestionPanel({ onClose }) {
    const [suggestions, setSuggestions] = useState([]);
    const [acceptedIds, setAcceptedIds] = useState(new Set());
    const suggestTasks = useSuggestTasks();
    const createTask = useCreateTask();
    const navigate = useNavigate();

    const handleFetch = () => {
        suggestTasks.mutate({}, {
            onSuccess: (data) => {
                setSuggestions(data);
                setAcceptedIds(new Set());
                if (data.length === 0) toast.info('No suggestions right now');
            },
            onError: () => toast.error('Failed to get AI suggestions'),
        });
    };

    const handleAccept = (suggestion, idx) => {
        createTask.mutate({
            title: suggestion.title,
            description: suggestion.description,
            priority: suggestion.priority || 'medium',
            due_date: suggestion.due_date || undefined,
            contact_id: suggestion.contact_id || undefined,
            source: 'ai_suggested',
        }, {
            onSuccess: () => {
                setAcceptedIds(prev => new Set([...prev, idx]));
                toast.success('Task created from suggestion');
            },
            onError: () => toast.error('Failed to create task'),
        });
    };

    const handleAcceptAll = () => {
        suggestions.forEach((s, idx) => {
            if (!acceptedIds.has(idx)) handleAccept(s, idx);
        });
    };

    const remaining = suggestions.filter((_, i) => !acceptedIds.has(i));

    return (
        <div className="mb-4 bg-gradient-to-br from-indigo-50 to-purple-50 border border-indigo-200 rounded-xl p-4">
            <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                    <Sparkles className="w-4 h-4 text-indigo-600" />
                    <span className="text-sm font-semibold text-stone-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                        AI Task Suggestions
                    </span>
                </div>
                <div className="flex items-center gap-2">
                    {suggestions.length === 0 && (
                        <button
                            onClick={handleFetch}
                            disabled={suggestTasks.isPending}
                            className="px-3 py-1.5 text-xs font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:opacity-50 flex items-center gap-1.5"
                        >
                            {suggestTasks.isPending ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
                            {suggestTasks.isPending ? 'Analyzing...' : 'Generate Suggestions'}
                        </button>
                    )}
                    <button onClick={onClose} className="text-stone-400 hover:text-stone-600">
                        <X className="w-4 h-4" />
                    </button>
                </div>
            </div>

            {suggestTasks.isPending && (
                <div className="flex items-center justify-center py-8">
                    <div className="text-center">
                        <Loader2 className="w-6 h-6 animate-spin text-indigo-500 mx-auto mb-2" />
                        <p className="text-xs text-stone-500">AI is analyzing your CRM data...</p>
                    </div>
                </div>
            )}

            {suggestions.length > 0 && (
                <>
                    {remaining.length > 1 && (
                        <div className="flex justify-end mb-2">
                            <button
                                onClick={handleAcceptAll}
                                className="px-2.5 py-1 text-[11px] font-medium text-indigo-700 bg-indigo-100 border border-indigo-200 rounded-md hover:bg-indigo-200"
                            >
                                Accept All ({remaining.length})
                            </button>
                        </div>
                    )}

                    <div className="space-y-2">
                        {suggestions.map((s, idx) => {
                            const accepted = acceptedIds.has(idx);
                            return (
                                <div
                                    key={idx}
                                    className={`bg-white rounded-lg border p-3 transition-all ${
                                        accepted ? 'border-emerald-200 opacity-60' : 'border-stone-200'
                                    }`}
                                >
                                    <div className="flex items-start justify-between gap-2">
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2 mb-1">
                                                <span className="text-sm font-medium text-stone-900">{s.title}</span>
                                                {s.priority && (
                                                    <span className={`text-[10px] px-1.5 py-0.5 rounded border ${
                                                        PRIORITY_COLORS[s.priority] || PRIORITY_COLORS.medium
                                                    }`}>
                                                        {s.priority}
                                                    </span>
                                                )}
                                            </div>
                                            {s.reasoning && (
                                                <p className="text-xs text-stone-500 mb-1.5 flex items-start gap-1">
                                                    <Lightbulb className="w-3 h-3 mt-0.5 shrink-0 text-amber-500" />
                                                    {s.reasoning}
                                                </p>
                                            )}
                                            <div className="flex items-center gap-3 text-[11px] text-stone-400">
                                                {s.due_date && (
                                                    <span className="flex items-center gap-1">
                                                        <Calendar className="w-3 h-3" />
                                                        {new Date(s.due_date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                                                    </span>
                                                )}
                                                {s.contact_name && (
                                                    <span className="flex items-center gap-1">
                                                        <User className="w-3 h-3" />
                                                        {s.contact_name}
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-1 shrink-0">
                                            {accepted ? (
                                                <span className="flex items-center gap-1 text-xs text-emerald-600 font-medium">
                                                    <Check className="w-3.5 h-3.5" /> Added
                                                </span>
                                            ) : (
                                                <>
                                                    <button
                                                        onClick={() => handleAccept(s, idx)}
                                                        disabled={createTask.isPending}
                                                        className="px-2.5 py-1 text-xs font-medium text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-md hover:bg-emerald-100"
                                                    >
                                                        Accept
                                                    </button>
                                                    <button
                                                        onClick={() => setAcceptedIds(prev => new Set([...prev, idx]))}
                                                        className="px-2.5 py-1 text-xs text-stone-400 hover:text-stone-600"
                                                    >
                                                        Dismiss
                                                    </button>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>

                    {remaining.length === 0 && (
                        <div className="text-center py-2 mt-2">
                            <p className="text-xs text-stone-500">All suggestions handled!</p>
                            <button
                                onClick={() => { setSuggestions([]); setAcceptedIds(new Set()); }}
                                className="text-xs text-indigo-600 hover:text-indigo-800 mt-1"
                            >
                                Generate more
                            </button>
                        </div>
                    )}
                </>
            )}

            {!suggestTasks.isPending && suggestions.length === 0 && (
                <p className="text-xs text-stone-500 text-center py-2">
                    Click "Generate Suggestions" to get AI-powered task recommendations based on your CRM data.
                </p>
            )}
        </div>
    );
}

/* ── Main Tasks Page ─────────────────────────────────────────────────────── */
export default function Tasks() {
    const [statusFilter, setStatusFilter] = useState('planned');
    const [priorityFilter, setPriorityFilter] = useState('');
    const [sortBy, setSortBy] = useState('due_date');
    const [sortDir, setSortDir] = useState('asc');
    const [selectedIds, setSelectedIds] = useState([]);
    const [showCompleted, setShowCompleted] = useState(false);
    const [showAISuggest, setShowAISuggest] = useState(false);
    const [campaignTask, setCampaignTask] = useState(null);

    const { data: tasks = [], isLoading, refetch: refetchTasks } = useTasks({
        status: statusFilter === 'all' ? undefined : statusFilter,
        priority: priorityFilter || undefined,
        sort_by: sortBy,
        sort_dir: sortDir,
    });
    const { data: summary } = useTasksSummary();
    const completeTask = useCompleteTask();
    const reopenTask = useReopenTask();
    const deleteTask = useDeleteTask();
    const bulkComplete = useBulkCompleteTasks();
    const bulkDelete = useBulkDeleteTasks();

    // Group tasks by date_group
    const grouped = useMemo(() => {
        const groups = {};
        GROUP_ORDER.forEach(g => { groups[g] = []; });
        groups['completed'] = [];

        tasks.forEach(t => {
            if (t.status === 'completed') {
                groups['completed'].push(t);
            } else {
                const g = t.date_group || 'later';
                if (!groups[g]) groups[g] = [];
                groups[g].push(t);
            }
        });
        return groups;
    }, [tasks]);

    const toggleSelect = (id) => {
        setSelectedIds(prev => prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]);
    };

    const handleComplete = (id) => {
        completeTask.mutate(id, {
            onError: () => toast.error('Failed to complete task'),
        });
    };

    const handleReopen = (id) => {
        reopenTask.mutate(id, {
            onError: () => toast.error('Failed to reopen task'),
        });
    };

    const handleDelete = (id) => {
        deleteTask.mutate(id, {
            onSuccess: () => toast.success('Task deleted'),
            onError: () => toast.error('Failed to delete'),
        });
        setSelectedIds(prev => prev.filter(i => i !== id));
    };

    const handleBulkComplete = () => {
        bulkComplete.mutate(selectedIds, {
            onSuccess: (data) => {
                toast.success(`${data.completed} tasks completed`);
                setSelectedIds([]);
            },
        });
    };

    const handleBulkDelete = () => {
        bulkDelete.mutate(selectedIds, {
            onSuccess: (data) => {
                toast.success(`${data.deleted} tasks deleted`);
                setSelectedIds([]);
            },
        });
    };

    return (
        <div className="p-4 sm:p-6 max-w-4xl mx-auto">
            {/* Header */}
            <div className="flex items-center justify-between mb-4">
                <div>
                    <h1 className="text-2xl font-bold text-stone-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                        Tasks
                    </h1>
                    {summary && (
                        <div className="flex items-center gap-3 mt-1">
                            {summary.overdue > 0 && (
                                <span className="text-xs font-medium text-red-600 bg-red-50 px-2 py-0.5 rounded-full border border-red-200">
                                    {summary.overdue} overdue
                                </span>
                            )}
                            <span className="text-xs text-stone-500">{summary.today} today</span>
                            <span className="text-xs text-stone-400">{summary.total_open} open</span>
                            {summary.completed_today > 0 && (
                                <span className="text-xs text-emerald-600">{summary.completed_today} done today</span>
                            )}
                        </div>
                    )}
                </div>
                <button
                    onClick={() => setShowAISuggest(!showAISuggest)}
                    className={`flex items-center gap-1.5 px-3 py-2 text-xs font-medium rounded-lg border transition-colors ${
                        showAISuggest
                            ? 'bg-indigo-600 text-white border-indigo-600'
                            : 'bg-white text-indigo-600 border-indigo-200 hover:bg-indigo-50'
                    }`}
                >
                    <Sparkles className="w-3.5 h-3.5" />
                    AI Suggest
                </button>
            </div>

            {/* Quick Add */}
            <div className="mb-4">
                <QuickAddBar />
            </div>

            {/* AI Suggestion Panel */}
            {showAISuggest && (
                <AISuggestionPanel onClose={() => setShowAISuggest(false)} />
            )}

            {/* Filter Bar */}
            <div className="flex items-center gap-2 mb-4 flex-wrap">
                <div className="flex items-center rounded-lg border border-stone-200 overflow-hidden text-xs">
                    {[
                        { key: 'planned', label: 'Open' },
                        { key: 'all', label: 'All' },
                    ].map(opt => (
                        <button
                            key={opt.key}
                            onClick={() => { setStatusFilter(opt.key); setSelectedIds([]); }}
                            className={`px-3 py-1.5 transition-colors ${
                                statusFilter === opt.key ? 'bg-stone-900 text-white' : 'bg-white text-stone-600 hover:bg-stone-50'
                            }`}
                        >
                            {opt.label}
                        </button>
                    ))}
                </div>

                <select
                    value={priorityFilter}
                    onChange={(e) => setPriorityFilter(e.target.value)}
                    className="px-2.5 py-1.5 text-xs border border-stone-200 rounded-lg bg-white focus:outline-none"
                >
                    <option value="">All priorities</option>
                    <option value="high">High</option>
                    <option value="medium">Medium</option>
                    <option value="low">Low</option>
                </select>

                <select
                    value={`${sortBy}-${sortDir}`}
                    onChange={(e) => {
                        const [sb, sd] = e.target.value.split('-');
                        setSortBy(sb);
                        setSortDir(sd);
                    }}
                    className="px-2.5 py-1.5 text-xs border border-stone-200 rounded-lg bg-white focus:outline-none"
                >
                    <option value="due_date-asc">Due date (earliest)</option>
                    <option value="due_date-desc">Due date (latest)</option>
                    <option value="priority-desc">Priority (high first)</option>
                    <option value="created_at-desc">Newest first</option>
                </select>
            </div>

            {/* Bulk Actions */}
            {selectedIds.length > 0 && (
                <div className="flex items-center gap-2 mb-4 p-2.5 bg-stone-100 rounded-lg border border-stone-200">
                    <span className="text-xs font-medium text-stone-600">{selectedIds.length} selected</span>
                    <button onClick={handleBulkComplete}
                        className="px-2.5 py-1 text-xs font-medium bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-md hover:bg-emerald-100">
                        Complete
                    </button>
                    <button onClick={handleBulkDelete}
                        className="px-2.5 py-1 text-xs font-medium bg-red-50 text-red-600 border border-red-200 rounded-md hover:bg-red-100">
                        Delete
                    </button>
                    <button onClick={() => setSelectedIds([])}
                        className="ml-auto text-xs text-stone-400 hover:text-stone-600">
                        Clear
                    </button>
                </div>
            )}

            {/* Task List */}
            {isLoading ? (
                <div className="flex items-center justify-center py-16">
                    <Loader2 className="w-6 h-6 animate-spin text-stone-400" />
                </div>
            ) : tasks.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-16 text-stone-400">
                    <CheckSquare className="w-12 h-12 mb-3 opacity-30" />
                    <p className="text-sm font-medium text-stone-500">No tasks yet</p>
                    <p className="text-xs mt-1">Add a task above to get started</p>
                </div>
            ) : (
                <>
                    {GROUP_ORDER.map(groupKey => (
                        <TaskGroup
                            key={groupKey}
                            groupKey={groupKey}
                            tasks={grouped[groupKey] || []}
                            selectedIds={selectedIds}
                            onSelect={toggleSelect}
                            onComplete={handleComplete}
                            onReopen={handleReopen}
                            onDelete={handleDelete}
                            onOpenCampaignTask={setCampaignTask}
                        />
                    ))}

                    {/* Completed section */}
                    {(statusFilter === 'all' || showCompleted) && grouped.completed?.length > 0 && (
                        <div className="mb-4">
                            <button
                                onClick={() => setShowCompleted(!showCompleted)}
                                className="flex items-center gap-2 px-4 py-2 w-full text-left rounded-t-lg border bg-stone-50 border-stone-200"
                            >
                                {showCompleted ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
                                <Check className="w-3.5 h-3.5 text-emerald-500" />
                                <span className="text-xs font-semibold text-stone-500" style={{ fontFamily: 'Manrope, sans-serif' }}>
                                    Completed
                                </span>
                                <span className="text-[10px] text-stone-400 ml-1">({grouped.completed.length})</span>
                            </button>
                            {showCompleted && (
                                <div className="bg-white border border-t-0 border-stone-200 rounded-b-lg overflow-hidden">
                                    {grouped.completed.map(task => (
                                        <TaskRow
                                            key={task.activity_id}
                                            task={task}
                                            isSelected={selectedIds.includes(task.activity_id)}
                                            onSelect={toggleSelect}
                                            onComplete={handleComplete}
                                            onReopen={handleReopen}
                                            onDelete={handleDelete}
                                        />
                                    ))}
                                </div>
                            )}
                        </div>
                    )}

                    {statusFilter === 'planned' && grouped.completed?.length > 0 && !showCompleted && (
                        <button
                            onClick={() => setShowCompleted(true)}
                            className="text-xs text-stone-400 hover:text-stone-600 flex items-center gap-1"
                        >
                            <Check className="w-3 h-3" /> Show {grouped.completed.length} completed tasks
                        </button>
                    )}
                </>
            )}

            {/* Campaign Task Execution Modal */}
            <CampaignTaskExecutionModal
                isOpen={!!campaignTask}
                onClose={() => setCampaignTask(null)}
                task={campaignTask}
                onComplete={() => { setCampaignTask(null); refetchTasks(); }}
            />
        </div>
    );
}
